import { Reference } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityFeedPostFragmentDoc,
  CommunityPostStatus,
  usePublishCommunityPostMutation,
} from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { feedVariantOf, variantForStatus } from '../lib/feedCacheVariants';

type PublishPostArgs = {
  communityId: CommunityId;
  postId: PostId;
  // Absent publishes now, a date schedules or reschedules, an explicit null
  // takes a scheduled post back to a plain draft.
  publishAt?: Date | null;
};

type PublishPostResult = { published: boolean };

const toastFor = (status: string | null | undefined): string => {
  if (status === CommunityPostStatus.Scheduled) return 'Post scheduled';
  if (status === CommunityPostStatus.Draft) return 'Moved back to drafts';
  return 'Post published';
};

export const usePublishPost = () => {
  const [publishPostMutation] = usePublishCommunityPostMutation();
  // One publish per post at a time. The menu items that call this stay
  // clickable while the mutation is in flight, and a second call would race the
  // cache write and double-count the analytics event.
  const inFlight = useRef(new Set<PostId>());

  return useCallback(
    async ({
      communityId,
      postId,
      publishAt,
    }: PublishPostArgs): Promise<PublishPostResult> => {
      if (inFlight.current.has(postId)) return { published: false };
      inFlight.current.add(postId);

      try {
        const { data } = await publishPostMutation({
          variables: {
            communityId,
            postId,
            ...(publishAt !== undefined && {
              publishAt: publishAt && publishAt.toISOString(),
            }),
          },
          update: (cache, { data: result }) => {
            const post = result?.publishPost;
            if (!post) return;

            const ref = cache.writeFragment({
              fragment: CommunityFeedPostFragmentDoc,
              fragmentName: 'CommunityFeedPost',
              data: post,
            });

            const target = variantForStatus(post.status);

            cache.modify({
              id: cache.identify({ __typename: 'Community', id: communityId }),
              fields: {
                posts: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  const present = existing.some(
                    (postRef) => readField('id', postRef) === postId,
                  );
                  if (feedVariantOf(storeFieldName) !== target) {
                    return present
                      ? existing.filter(
                          (postRef) => readField('id', postRef) !== postId,
                        )
                      : existing;
                  }
                  if (present || !ref) return existing;
                  return [ref, ...existing];
                },
              },
            });
          },
        });

        const status = data?.publishPost?.status;
        toast.success(toastFor(status));
        return { published: status === CommunityPostStatus.Published };
      } catch {
        toast.error('Something went wrong');
        return { published: false };
      } finally {
        inFlight.current.delete(postId);
      }
    },
    [publishPostMutation],
  );
};
